import {
  companyContextRecords,
  getProfileSources,
  peopleProfiles,
} from "../data/peopleProfiles";
import { buildAbsoluteUrl } from "../data/siteMeta";
import { pickSources } from "../data/sourceRegistry";

const pageNotice =
  "This page is an indexable public-record summary. It uses professional profile details, public listing records, and disputed complaint materials. It does not state that unresolved allegations are true or that any person violated the law unless a cited regulator or court record says so.";

const commonResources = pickSources(["cfpbComplaintPortal", "nmlsConsumerAccess", "regulationZ"]);

const personLinks = {
  robShell: [
    { label: "Rob Shell Mutual of Omaha Mortgage", to: "/rob-shell-mutual-of-omaha-mortgage" },
    { label: "Robert Hillman Shell NMLS 2226086", to: "/robert-hillman-shell-nmls-2226086" },
    { label: "Rob Shell DSCR steering allegations", to: "/rob-shell-dscr-loan-steering-allegations" },
    { label: "Rob Shell LinkedIn public record", to: "/rob-shell-linkedin-public-record" },
  ],
  vickiSmith: [
    { label: "Vicki Smith PulteGroup New Home Sales Consultant", to: "/vicki-smith-pultegroup-new-home-sales-consultant" },
    { label: "Vicki Smith Nashville new home sales", to: "/vicki-smith-nashville-new-home-sales" },
    { label: "Vicki Smith Legacy South Soren timeline", to: "/vicki-smith-legacy-south-soren-timeline" },
    { label: "Vicki Smith LinkedIn public record", to: "/vicki-smith-linkedin-public-record" },
  ],
};

const peopleIndexLinks = [...personLinks.robShell, ...personLinks.vickiSmith];

function personJsonLd(profile, slug) {
  const professionalContext = profile.professionalContext;
  const license = profile.license;

  return {
    "@context": "https://schema.org",
    "@type": "Person",
    "@id": `${buildAbsoluteUrl(slug)}#person`,
    name: profile.fullName,
    alternateName: profile.aliases,
    ...(professionalContext
      ? {
          jobTitle: professionalContext.role,
          worksFor: {
            "@type": "Organization",
            name: professionalContext.organization,
          },
        }
      : {}),
    ...(license?.status === "source-established"
      ? { identifier: license.displayValue }
      : {}),
    sameAs: profile.linkedInUrl ? [profile.linkedInUrl] : undefined,
    subjectOf: {
      "@type": "WebPage",
      name: `${profile.displayName} public-record dossier page`,
      url: buildAbsoluteUrl(slug),
    },
  };
}

function linkSection(profileKey) {
  return {
    heading: "Related person pages",
    paragraphs: [
      "These pages separate public profile facts, complaint allegations, and record-request issues so readers and search engines can find the relevant context without mixing source types.",
    ],
    links: personLinks[profileKey],
  };
}

function sourceSection(profile) {
  return {
    heading: "Source handling",
    paragraphs: [
      `${profile.displayName} is discussed here using public professional sources and complaint-record context only.`,
      profile.privacyNote,
    ],
  };
}

function licenseFact(profile) {
  return {
    label: profile.license.label,
    value: profile.license.displayValue,
  };
}

function personContext(profile, companyContext) {
  return {
    recordId: profile.id,
    license: profile.license,
    professionalContext: profile.professionalContext,
    ...(companyContext ? { companyContext } : {}),
  };
}

const rob = peopleProfiles.robShell;
const vicki = peopleProfiles.vickiSmith;
const mutualMortgageContext = companyContextRecords.mutualOfOmahaMortgage;
const pulteGroupContext = companyContextRecords.pulteGroup;

export const personSeoPages = [
  {
    slug: "/people-index",
    title: "People Index: Rob Shell and Vicki Smith",
    linkTitle: "People Index",
    metaTitle: "People Index for Rob Shell and Vicki Smith | CFPB Mortgage Complaint",
    description:
      "Indexable guide to Rob Shell, Robert Hillman Shell, and Vicki Smith public profile pages, LinkedIn references, complaint roles, and source-backed dossier pages.",
    subheading:
      "A search-friendly directory of named people, public roles, sources, and complaint-context pages.",
    notice: pageNotice,
    peopleContext: [
      personContext(rob, mutualMortgageContext),
      personContext(vicki, pulteGroupContext),
    ],
    facts: [
      { label: "Rob Shell", value: "Robert Hillman Shell, NMLS 2226086, Mutual of Omaha Mortgage" },
      {
        label: "Vicki Smith",
        value:
          "PulteGroup public-profile context; license number not established by current cited sources",
      },
      { label: "Privacy rule", value: "Public professional identifiers only; no personal contact details" },
    ],
    sections: [
      {
        heading: "Rob Shell page set",
        paragraphs: [
          "The Rob Shell pages connect Robert Hillman Shell, NMLS 2226086, Mutual of Omaha Mortgage, Brentwood, and the complaint's DSCR loan-option allegations.",
        ],
        links: personLinks.robShell,
      },
      {
        heading: "Vicki Smith page set",
        paragraphs: [
          "The Vicki Smith pages connect her public LinkedIn profile, PulteGroup New Home Sales Consultant role, Nashville Metropolitan Area reference, Legacy South, Soren, and the complaint's closing-pressure allegations.",
        ],
        links: personLinks.vickiSmith,
      },
      {
        heading: "How to read person pages",
        paragraphs: [
          "Public profile facts identify professional roles and sources. Complaint pages identify allegations and disputed conduct. Record-request pages identify what documents would confirm or refute the allegations.",
        ],
      },
    ],
    sources: [
      ...getProfileSources(rob),
      ...getProfileSources(vicki),
    ],
    resources: commonResources,
    additionalJsonLd: {
      "@context": "https://schema.org",
      "@type": "CollectionPage",
      name: "People Index: Rob Shell and Vicki Smith",
      url: buildAbsoluteUrl("/people-index"),
      mainEntity: {
        "@type": "ItemList",
        itemListElement: peopleIndexLinks.map((link, index) => ({
          "@type": "ListItem",
          position: index + 1,
          name: link.label,
          url: buildAbsoluteUrl(link.to),
        })),
      },
    },
  },
  {
    slug: "/rob-shell-mutual-of-omaha-mortgage",
    title: "Rob Shell and Mutual of Omaha Mortgage",
    linkTitle: "Rob Shell / Mutual",
    metaTitle: "Rob Shell Mutual of Omaha Mortgage | CFPB Mortgage Complaint",
    description:
      "Public profile and complaint-context page connecting Rob Shell, Robert Hillman Shell, Mutual of Omaha Mortgage, Brentwood, and the Dupont loan dispute.",
    subheading:
      "A focused page for searches connecting Rob Shell to Mutual of Omaha Mortgage and the complaint record.",
    notice: pageNotice,
    personContext: personContext(rob, mutualMortgageContext),
    facts: [
      { label: "Name used publicly", value: "Rob Shell / Robert Hillman Shell" },
      { label: "Company", value: "Mutual of Omaha Mortgage" },
      { label: "Office context", value: "Brentwood, Tennessee" },
      licenseFact(rob),
      { label: "LinkedIn profile", value: rob.linkedInPath },
    ],
    sections: [
      {
        heading: "Public role",
        paragraphs: [
          "Mutual of Omaha Mortgage's public loan officer page identifies Rob Shell as a mortgage loan originator.",
          "The dossier uses Robert Hillman Shell as the formal name appearing in complaint materials and Rob Shell as the public-facing name.",
        ],
      },
      {
        heading: "Complaint connection",
        paragraphs: [
          rob.complaintContext,
          "The complaint alleges that loan-option presentation and closing timing should be reviewed for possible steering concerns. Mutual disputes wrongdoing and states its review did not identify coercion, steering, excessive fees, or improper compensation.",
        ],
      },
      linkSection("robShell"),
      sourceSection(rob),
    ],
    sources: getProfileSources(rob),
    resources: commonResources,
    additionalJsonLd: personJsonLd(rob, "/rob-shell-mutual-of-omaha-mortgage"),
  },
  {
    slug: "/robert-hillman-shell-nmls-2226086",
    title: "Robert Hillman Shell NMLS 2226086",
    linkTitle: "Robert Hillman Shell NMLS",
    metaTitle: "Robert Hillman Shell NMLS 2226086 | CFPB Mortgage Complaint",
    description:
      "Search-focused public-record page for Robert Hillman Shell, NMLS 2226086, Rob Shell, and the Mutual of Omaha Mortgage complaint record.",
    subheading:
      "A public identifier page for NMLS and complaint-review searches.",
    notice: pageNotice,
    personContext: personContext(rob, mutualMortgageContext),
    facts: [
      { label: "Formal name", value: "Robert Hillman Shell" },
      { label: "Public name", value: "Rob Shell" },
      licenseFact(rob),
      { label: "Public title", value: "Mortgage Loan Originator" },
    ],
    sections: [
      {
        heading: "Identifier context",
        paragraphs: [
          "The NMLS number is included so readers can distinguish the Rob Shell referenced in this dossier from other people with similar names.",
          "Readers should use NMLS Consumer Access for current licensing lookup because licensing data can change over time.",
        ],
      },
      {
        heading: "Why this identifier matters",
        paragraphs: [
          "The complaint asks regulators to review the loan officer's product-option presentation, rate-sheet availability, and compensation context for the two Dupont transactions.",
          "The NMLS identifier helps attach those review requests to the correct public mortgage professional record.",
        ],
      },
      linkSection("robShell"),
      sourceSection(rob),
    ],
    sources: getProfileSources(rob),
    resources: commonResources,
    additionalJsonLd: personJsonLd(rob, "/robert-hillman-shell-nmls-2226086"),
  },
  {
    slug: "/rob-shell-dscr-loan-steering-allegations",
    title: "Rob Shell DSCR Loan Steering Allegations",
    linkTitle: "Rob Shell DSCR Allegations",
    metaTitle: "Rob Shell DSCR Loan Steering Allegations | CFPB Mortgage Complaint",
    description:
      "Complaint allegations involving Rob Shell, DSCR loan presentation, non-QM interest-only financing, and requested anti-steering review.",
    subheading:
      "A legally careful allegation page for the disputed DSCR loan-choice issue.",
    notice: pageNotice,
    personContext: personContext(rob, mutualMortgageContext),
    facts: [
      { label: "Issue", value: "DSCR / non-QM interest-only loan-option presentation" },
      { label: "Complaint theory", value: "Alternatives were requested but not meaningfully presented" },
      { label: "Company position", value: "Mutual disputes coercion, steering, and improper compensation" },
    ],
    sections: [
      {
        heading: "Allegation stated in the complaint",
        paragraphs: [
          "The complaint alleges that Rob Shell represented the DSCR interest-only product as the only practical loan path for the Dupont properties.",
          "The complaint further alleges that requested side-by-side alternatives, rate sheets, and conventional comparisons were not provided during the active decision window.",
        ],
      },
      {
        heading: "Records needed to test the allegation",
        list: [
          "Lock-date rate sheets and product matrices.",
          "Written conventional eligibility analysis or decline rationale.",
          "Communications showing requests for alternative products.",
          "Loan-level compensation records for DSCR and alternative channels.",
        ],
      },
      linkSection("robShell"),
    ],
    sources: pickSources(["robShellOfficial", "robShellLinkedIn", "regulationZ"]),
    resources: commonResources,
    additionalJsonLd: personJsonLd(rob, "/rob-shell-dscr-loan-steering-allegations"),
  },
  {
    slug: "/rob-shell-linkedin-public-record",
    title: "Rob Shell LinkedIn Public Record",
    linkTitle: "Rob Shell LinkedIn",
    metaTitle: "Rob Shell LinkedIn Public Record | CFPB Mortgage Complaint",
    description:
      "Public LinkedIn reference page for Rob Shell / Robert Hillman Shell and the complaint-context links in this dossier.",
    subheading:
      "A source page for the Rob Shell LinkedIn profile and related dossier pages.",
    notice: pageNotice,
    personContext: personContext(rob, mutualMortgageContext),
    facts: [
      { label: "LinkedIn URL", value: rob.linkedInPath },
      licenseFact(rob),
      { label: "Source label", value: "LinkedIn profile: Robert Hillman Shell" },
      { label: "Dossier role", value: "Public profile source and identity-disambiguation reference" },
    ],
    sections: [
      {
        heading: "What this source establishes",
        paragraphs: [
          "The LinkedIn source is used as a public profile reference for Rob Shell / Robert Hillman Shell.",
          "The source is not used by itself to prove the complaint allegations. It supports identity, public-role, and search-discovery context.",
        ],
      },
      {
        heading: "How it links to the complaint record",
        paragraphs: [
          "The complaint-specific pages separately describe the disputed loan-option presentation and records requested for independent review.",
        ],
      },
      linkSection("robShell"),
    ],
    sources: pickSources(["robShellLinkedIn", "robShellOfficial"]),
    resources: commonResources,
    additionalJsonLd: personJsonLd(rob, "/rob-shell-linkedin-public-record"),
  },
  {
    slug: "/vicki-smith-pultegroup-new-home-sales-consultant",
    title: "Vicki Smith PulteGroup New Home Sales Consultant",
    linkTitle: "Vicki Smith PulteGroup",
    metaTitle: "Vicki Smith PulteGroup New Home Sales Consultant | CFPB Mortgage Complaint",
    description:
      "Public profile page for Vicki Smith, PulteGroup New Home Sales Consultant, Nashville Metropolitan Area, and related complaint-context records.",
    subheading:
      "A public-profile page linking Vicki Smith's PulteGroup role to the dossier's builder-sales context.",
    notice: pageNotice,
    personContext: personContext(vicki, pulteGroupContext),
    facts: [
      { label: "Name", value: "Vicki Smith" },
      { label: "Public title", value: "New Home Sales Consultant" },
      { label: "Employer listed in public profile", value: "PulteGroup" },
      { label: "Public area listed", value: "Nashville Metropolitan Area" },
      licenseFact(vicki),
      { label: "LinkedIn profile", value: vicki.linkedInPath },
    ],
    sections: [
      {
        heading: "Public profile facts",
        paragraphs: [
          "The public LinkedIn profile supplied for review identifies Vicki Smith as a New Home Sales Consultant with PulteGroup in the Nashville Metropolitan Area.",
          "This page treats that as public professional context and does not infer the exact legal relationship between PulteGroup and any Tennessee operating entity beyond what the public source states.",
        ],
      },
      {
        heading: "Why it matters to the dossier",
        paragraphs: [
          "The complaint identifies Vicki Smith in the Legacy South sales context and alleges she communicated closing pressure tied to earnest-money risk.",
          "The later PulteGroup public profile is relevant to a public timeline of new-home sales roles, not as proof that PulteGroup or Vicki Smith committed wrongdoing.",
        ],
      },
      linkSection("vickiSmith"),
      sourceSection(vicki),
    ],
    sources: getProfileSources(vicki),
    resources: commonResources,
    additionalJsonLd: personJsonLd(vicki, "/vicki-smith-pultegroup-new-home-sales-consultant"),
  },
  {
    slug: "/vicki-smith-nashville-new-home-sales",
    title: "Vicki Smith Nashville New Home Sales",
    linkTitle: "Vicki Smith Nashville",
    metaTitle: "Vicki Smith Nashville New Home Sales | CFPB Mortgage Complaint",
    description:
      "Search-focused page for Vicki Smith, Nashville Metropolitan Area, new home sales, PulteGroup, Legacy South, and the Soren complaint timeline.",
    subheading:
      "A Nashville-area new-home-sales context page for Vicki Smith and the complaint record.",
    notice: pageNotice,
    personContext: personContext(vicki, pulteGroupContext),
    facts: [
      { label: "Public area", value: "Nashville Metropolitan Area" },
      { label: "Public sales role", value: "New Home Sales Consultant" },
      licenseFact(vicki),
      { label: "Complaint context", value: "Legacy South / Soren closing-pressure allegations" },
    ],
    sections: [
      {
        heading: "Nashville-area public profile context",
        paragraphs: [
          "The LinkedIn profile identifies Vicki Smith in the Nashville Metropolitan Area and lists a new-home-sales role.",
          "Homes.com / Realtracs listing context separately identifies Vicki Smith with Pulte Homes Tennessee on a 2025 Soren listing.",
        ],
      },
      {
        heading: "Connection to Soren and Legacy South",
        paragraphs: [
          "The complaint concerns two units in the Soren development at 700 N Dupont Ave in Madison, Tennessee.",
          "Complaint materials identify Vicki Smith in the Legacy South sales context and allege closing pressure affecting the borrower's practical financing choices.",
        ],
      },
      linkSection("vickiSmith"),
    ],
    sources: getProfileSources(vicki),
    resources: commonResources,
    additionalJsonLd: personJsonLd(vicki, "/vicki-smith-nashville-new-home-sales"),
  },
  {
    slug: "/vicki-smith-legacy-south-soren-timeline",
    title: "Vicki Smith Legacy South and Soren Timeline",
    linkTitle: "Vicki Smith Soren Timeline",
    metaTitle: "Vicki Smith Legacy South Soren Timeline | CFPB Mortgage Complaint",
    description:
      "Timeline page connecting Vicki Smith, Legacy South, Soren, 700 N Dupont Ave, Pulte Homes Tennessee, and disputed closing-pressure allegations.",
    subheading:
      "A complaint-context timeline for Vicki Smith, Legacy South, and Soren public records.",
    notice: pageNotice,
    personContext: personContext(vicki, pulteGroupContext),
    facts: [
      { label: "Complaint role", value: "Legacy South sales counselor / affiliate broker in complaint materials" },
      { label: "Development", value: "Soren, 700 N Dupont Ave, Madison, Tennessee" },
      { label: "Later public listing reference", value: "Pulte Homes Tennessee on a 2025 Soren listing" },
      licenseFact(vicki),
    ],
    sections: [
      {
        heading: "Complaint timeline context",
        paragraphs: [
          "The complaint alleges Vicki Smith facilitated contract documentation and closing coordination and warned that withdrawing could place earnest money at risk.",
          "Those statements remain complaint allegations unless supported by independent records.",
        ],
      },
      {
        heading: "Public listing context",
        paragraphs: [
          "Homes.com / Realtracs identifies Vicki Smith with Pulte Homes Tennessee on a public listing record for 700 N Dupont Ave Unit 303.",
          "The same record is used as public timeline context and is not treated as proof of the Dupont loan allegations.",
        ],
      },
      linkSection("vickiSmith"),
    ],
    sources: getProfileSources(vicki),
    resources: commonResources,
    additionalJsonLd: personJsonLd(vicki, "/vicki-smith-legacy-south-soren-timeline"),
  },
  {
    slug: "/vicki-smith-linkedin-public-record",
    title: "Vicki Smith LinkedIn Public Record",
    linkTitle: "Vicki Smith LinkedIn",
    metaTitle: "Vicki Smith LinkedIn Public Record | CFPB Mortgage Complaint",
    description:
      "Public LinkedIn reference page for Vicki Smith, PulteGroup, New Home Sales Consultant, Nashville Metropolitan Area, and complaint-context links.",
    subheading:
      "A source page for Vicki Smith's LinkedIn profile and related dossier pages.",
    notice: pageNotice,
    personContext: personContext(vicki, pulteGroupContext),
    facts: [
      { label: "LinkedIn URL", value: vicki.linkedInPath },
      { label: "Public title", value: "New Home Sales Consultant" },
      { label: "Employer listed in public profile", value: "PulteGroup" },
      { label: "Public area listed", value: "Nashville Metropolitan Area" },
      licenseFact(vicki),
    ],
    sections: [
      {
        heading: "What this source establishes",
        paragraphs: [
          "The LinkedIn source is used as a public profile reference for Vicki Smith's name, PulteGroup role, New Home Sales Consultant title, and Nashville Metropolitan Area location.",
          "The source is not used by itself to prove the complaint allegations. It supports identity, public-role, and search-discovery context.",
        ],
      },
      {
        heading: "How it links to the complaint record",
        paragraphs: [
          "The complaint-specific pages separately discuss Legacy South, Soren, earnest-money pressure allegations, and the records needed to test the sales-pressure narrative.",
        ],
      },
      linkSection("vickiSmith"),
    ],
    sources: pickSources(["vickiSmithLinkedIn", "pulteGroupLinkedIn", "homesSorenUnit303"]),
    resources: commonResources,
    additionalJsonLd: personJsonLd(vicki, "/vicki-smith-linkedin-public-record"),
  },
];
